/**
 * _pmChartBlocks.jsx : sous-blocs visuels de PMChart (privés au dossier
 * charts/, convention D52). Sans état ni logique métier.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateLocale } from '../utils/formatters';

const PANEL_COLORS = {
  'PM1': { light: '#00CED1', dark: '#40E0D0' }, // Turquoise
  'PM2.5': { light: '#9370DB', dark: '#BA87FF' }, // Violet/Purple
  'PM10': { light: '#FF1493', dark: '#FF69B4' }, // Magenta/Deep Pink
};

/** Panneau min / moyenne / max au-dessus du graphique, PM avec données seulement. */
export function PmStatsPanel({ entries, darkMode }) {
  const { t } = useTranslation();
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', marginBottom: '16px' }}>
      {entries.map(({ label, stats }) => {
        const color = PANEL_COLORS[label][darkMode ? 'dark' : 'light'];
        return (
          <div key={label} style={{
            padding: '12px',
            background: darkMode ? 'rgba(26, 26, 46, 0.6)' : 'rgba(255, 255, 255, 0.9)',
            borderRadius: '8px',
            border: `2px solid ${color}`,
            boxShadow: darkMode ? 'none' : '0 2px 8px rgba(0,0,0,0.08)',
          }}>
            <div style={{ fontSize: '11px', fontWeight: '600', color, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {label}
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: darkMode ? '#E8EDF2' : '#4A5568' }}>
              <span>{t('charts.stats.min')} <strong>{stats.min.toFixed(1)}</strong></span>
              <span>{t('charts.stats.avg')} <strong>{stats.avg.toFixed(1)}</strong></span>
              <span>{t('charts.stats.max')} <strong>{stats.max.toFixed(1)}</strong></span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** Bulle « Ajouter une annotation » au point cliqué. */
export function QuickAnnotPopover({ quickAnnot, darkMode, onAdd, onClose }) {
  const { t } = useTranslation();
  return (
    <div style={{
      position: 'absolute', left: quickAnnot.x, top: quickAnnot.y,
      transform: 'translateX(-50%)',
      background: darkMode ? '#1E1E32' : '#fff',
      border: `1.5px solid #1B77F1`,
      borderRadius: '8px', padding: '8px 12px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
      zIndex: 10, whiteSpace: 'nowrap', pointerEvents: 'all'
    }}>
      <div style={{ fontSize: '11px', color: '#6B7280', marginBottom: '6px', fontFamily: 'Inter, sans-serif' }}>
        {formatDateLocale(quickAnnot.timestamp, { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
        {quickAnnot.pm25 != null && ` · PM2.5 ${quickAnnot.pm25.toFixed(1)} µg/m³`}
      </div>
      <div style={{ display: 'flex', gap: '6px' }}>
        <button onClick={(e) => { e.stopPropagation(); onAdd(); }}
          style={{ padding: '4px 10px', borderRadius: '6px', background: '#1B77F1', color: '#fff',
            border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: 'Inter, sans-serif' }}>
          {t('session.annotations.quickAdd')}
        </button>
        <button onClick={(e) => { e.stopPropagation(); onClose(); }}
          aria-label={t('common.close')}
          style={{ padding: '4px 8px', borderRadius: '6px', background: 'none',
            border: '1px solid #e0e0e0', cursor: 'pointer', fontSize: '12px', color: '#6B7280' }}>
          ×
        </button>
      </div>
    </div>
  );
}

/** Mode d'emploi du zoom (tactile / bureau) et note de bas de graphique. */
export function PmChartFooter({ darkMode }) {
  const { t } = useTranslation();
  return (
    <>
      <div style={{
        marginTop: '12px',
        padding: '8px 12px',
        background: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(27, 119, 241, 0.05)',
        borderRadius: '6px',
        border: darkMode ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(27, 119, 241, 0.15)',
        fontSize: '13px',
        color: darkMode ? '#E8EDF2' : '#4A5568',
        textAlign: 'center',
        lineHeight: '1.5'
      }}>
        <span style={{ display: 'none' }} className="mobile-only">
          {t('common.zoom.touch')}
        </span>
        <span className="desktop-only">
          {t('common.zoom.desktop')}
        </span>
      </div>
      <p style={{ fontFamily: "'Inter', sans-serif", fontSize: '11px', color: darkMode ? '#B8BFC7' : '#6B7280', margin: '8px 0 0', fontStyle: 'italic', lineHeight: '1.5' }}>
        {t('charts.pm.footnote')}
      </p>
    </>
  );
}
